import { useState, useEffect } from "react";
import { format } from "date-fns";
import { toast } from "react-toastify";
import SidePanel from "./sidePanel";
import { UserIcon1Svg } from "../svgs/userIcon1Svg";
import { CalendarIcon1Svg } from "../svgs/calendarIcon1Svg";
import { ClockIcon1Svg } from "../svgs/clockIcon1Svg";
import { redirectToWhatsapp } from "../utils/redirectToWhatsapp";
import CommentModal from "./commentModal";
import APPOINTMENTS from "../api/services/APPOINTMENTS";
import SCHEDULE from "../api/services/SCHEDULE";
import styles from "../styles/_components/appointmentDetailsModal.module.scss";
import { useNavigate } from "react-router-dom";
import { paths } from "../routes/paths";

const AppointmentDetailsModal = ({ data, setIsOpen, setSuccess, onClientClick }) => {
  const navigate = useNavigate();
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);

  // Reschedule state
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(
    format(new Date(data.start), "yyyy-MM-dd")
  );
  const [timeslots, setTimeslots] = useState([]);
  const [timeslotsLoading, setTimeslotsLoading] = useState(false);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  // Fetch timeslots when date changes in reschedule mode
  useEffect(() => {
    if (!rescheduleMode || !rescheduleDate) return;

    const fetchTimeslots = async () => {
      setTimeslotsLoading(true);
      setTimeslots([]);
      try {
        const res = await SCHEDULE.get_timeslots({
          employeeId: data.employeeId,
          serviceId: data.serviceId,
          date: rescheduleDate,
        });
        const raw = res.data?.data || res.data || {};
        // API returns {records: [...], detailedRecords: [...]} or an array
        const slots = Array.isArray(raw)
          ? raw
          : raw.detailedRecords || raw.records || [];
        // Normalize: each slot might be a string or {slot: "09:00", available: true, ...}
        const normalized = slots
          .filter(s => typeof s === "string" || s?.available !== false)
          .map(s => (typeof s === "string" ? s : s?.slot || s?.time || ""))
          .filter(Boolean);
        setTimeslots(normalized);
      } catch (error) {
        console.log(error);
        toast.error("שגיאה בטעינת שעות פנויות");
      }
      setTimeslotsLoading(false);
    };

    fetchTimeslots();
  }, [rescheduleMode, rescheduleDate, data.employeeId, data.serviceId]);

  // Handle reschedule
  const handleReschedule = async (timeSlot) => {
    setRescheduleLoading(true);
    try {
      await APPOINTMENTS.reschedule({
        id: data.id,
        date: rescheduleDate,
        timeSlot,
      });
      toast.success("התור שובץ מחדש בהצלחה");
      setSuccess(true);
      setIsOpen(false);
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "שגיאה בשינוי זמן התור");
    }
    setRescheduleLoading(false);
  };

  // SEND MANUAL REMINDER
  const handleManualReminder = async () => {
    let payload = {
      userId: data.userId,
      appointmentId: data.id,
    };
    try {
      const res = await APPOINTMENTS.send_reminder(payload);
      toast.success(res.data.message);
      setIsOpen(false);
    } catch (error) {
      console.log(error);
      toast.error(error.response.data.messsage);
      setIsOpen(false);
    }
  };

  // CANCEL APPOINTMENT
  const cancelAppointment = async () => {
    let payload = {
      id: data.id,
      status: 3,
    };
    try {
      const res = await APPOINTMENTS.cancel_appointment(payload);
      setSuccess(true);
      toast.success(res.data.message);
      setIsOpen(false);
    } catch (error) {
      console.log(error);
      toast.error(error.response.data.messsage);
    }
  };

  return (
    <>
      <SidePanel setIsOpen={setIsOpen} title={rescheduleMode ? "שינוי זמן שיבוץ" : "פרטי תור"}>
        <div className={styles.container}>

          {rescheduleMode ? (
            /* ── Reschedule mode ── */
            <div className={styles.rescheduleContainer}>
              {/* Client name header */}
              <div className={styles.rescheduleHeader}>
                <div className={styles.avatar}>
                  {(data.customerName || "?")[0].toUpperCase()}
                </div>
                <div className={styles.rescheduleHeaderInfo}>
                  <span className={styles.rescheduleClientName}>{data.customerName}</span>
                  <span className={styles.rescheduleServiceName}>{data.title}</span>
                </div>
              </div>

              {/* Date picker */}
              <div className={styles.rescheduleField}>
                <label className={styles.rescheduleLabel}>בחר תאריך</label>
                <input
                  type="date"
                  className={styles.rescheduleDateInput}
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  min={format(new Date(), "yyyy-MM-dd")}
                />
              </div>

              {/* Timeslots */}
              <div className={styles.rescheduleField}>
                <label className={styles.rescheduleLabel}>שעות פנויות</label>

                {timeslotsLoading ? (
                  <div className={styles.rescheduleSpinner}>
                    <div className={styles.spinner} />
                    <span>טוען שעות פנויות...</span>
                  </div>
                ) : timeslots.length === 0 ? (
                  <div className={styles.rescheduleEmpty}>
                    אין שעות פנויות בתאריך זה
                  </div>
                ) : (
                  <div className={styles.timeslotsGrid}>
                    {timeslots.map((slot) => (
                      <button
                        key={slot}
                        className={styles.timeslotBtn}
                        onClick={() => handleReschedule(slot)}
                        disabled={rescheduleLoading}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Cancel reschedule */}
              <button
                className={styles.rescheduleCancel}
                onClick={() => setRescheduleMode(false)}
                disabled={rescheduleLoading}
              >
                חזרה לפרטי התור
              </button>
            </div>
          ) : (
            /* ── Normal appointment details ── */
            <>
              {/* ── Service badge ── */}
              <div className={styles.serviceBadge}>
                <span className={styles.serviceIcon}>✂️</span>
                <span className={styles.serviceName}>{data.title}</span>
              </div>

              {/* ── Info cards ── */}
              <div className={styles.infoGrid}>
                <div
                  className={styles.infoCard}
                  onClick={() => {
                    if (onClientClick && data.userId) {
                      onClientClick({
                        userId: data.userId,
                        customerName: data.customerName,
                        phone: data.phone,
                      });
                    }
                  }}
                  style={onClientClick ? { cursor: "pointer" } : {}}
                >
                  <div className={styles.infoLabel}>לקוח</div>
                  <div className={styles.infoValue}>
                    <div className={styles.avatar}>{(data.customerName || "?")[0].toUpperCase()}</div>
                    <span>{data.customerName}</span>
                  </div>
                </div>

                <div className={styles.infoCard}>
                  <div className={styles.infoLabel}>תאריך</div>
                  <div className={styles.infoValue}>
                    <CalendarIcon1Svg fill="#DE1468" />
                    <span>{format(new Date(data.start), "dd/MM/yyyy")}</span>
                  </div>
                </div>

                <div className={styles.infoCard}>
                  <div className={styles.infoLabel}>שעה</div>
                  <div className={styles.infoValue}>
                    <ClockIcon1Svg fill="#DE1468" />
                    <span>{format(new Date(data.start), "HH:mm")} – {format(new Date(data.end), "HH:mm")}</span>
                  </div>
                </div>
              </div>

              {/* ── Actions ── */}
              <div className={styles.actions}>
                {data.phone && (
                  <button className={styles.actionBtn} onClick={() => redirectToWhatsapp(data.phone)}>
                    <img src="/assets/whatsapp-icon-1.svg" alt="whatsapp" />
                    <span>שלח הודעת וואטסאפ</span>
                  </button>
                )}

                <button className={styles.actionBtn} onClick={() => setIsCommentModalOpen(true)}>
                  <img src="/assets/write-icon-1.svg" alt="write" />
                  <span>הוספת הערה לתור</span>
                </button>

                <button className={styles.actionBtn} onClick={handleManualReminder}>
                  <img src="/assets/paper-plane-icon-1.svg" alt="paper-plane" />
                  <span>שליחת תזכורת ללקוח/ה</span>
                </button>

                <button className={styles.actionBtn} onClick={() => navigate(paths.clientMemberShip(data.userId))}>
                  <img src="/assets/card-icon-1.svg" alt="card" />
                  <span>כרטיסיות ומנויים</span>
                </button>

                <button className={styles.actionBtn}>
                  <img src="/assets/tinder-icon-1.svg" alt="tinder" />
                  <span>תיק לקוח</span>
                </button>

                <button className={styles.actionBtn} onClick={() => setRescheduleMode(true)}>
                  <img src="/assets/calendar-icon-1.svg" alt="reschedule" />
                  <span>שינוי זמן שיבוץ</span>
                </button>

                <button className={`${styles.actionBtn} ${styles.cancelBtn}`} onClick={cancelAppointment}>
                  <img src="/assets/remove-icon-1.svg" alt="remove" />
                  <span>ביטול תור</span>
                </button>
              </div>

              {/* ── Created at ── */}
              <div className={styles.createdAt}>
                נוצר: {format(new Date(data.createdAt), "dd/MM/yyyy")}
              </div>
            </>
          )}
        </div>
      </SidePanel>

      {/* MODALS */}
      {isCommentModalOpen && (
        <CommentModal
          setIsOpen={setIsCommentModalOpen}
          appointmentId={data.id}
        />
      )}
    </>
  );
};

export default AppointmentDetailsModal;
